import {createSlice, createAsyncThunk, PayloadAction} from '@reduxjs/toolkit';
import {
  apartmentServiceFind,
  buildingServiceFind,
  roomServiceFind,
} from 'src/apis/autoGenerate/services';
import {Apartment, Building, Room, RoomStatusEnum} from 'src/apis/autoGenerate/types';
import {RootState} from '../store';

// Define interfaces for our cached data
interface PropertyCacheState {
  apartments: Apartment[];
  buildings: {[apartmentId: string]: Building[]};
  rooms: {[buildingId: string]: Room[]};
  statistics: {
    totalApartments: number;
    totalRooms: number;
    occupiedRooms: number;
    vacantRooms: number;
    totalTenants: number;
  };
  lastFetched: number | null;
  loading: boolean;
  error: string | null;
}

const initialState: PropertyCacheState = {
  apartments: [],
  buildings: {},
  rooms: {},
  statistics: {
    totalApartments: 0,
    totalRooms: 0,
    occupiedRooms: 0,
    vacantRooms: 0,
    totalTenants: 0,
  },
  lastFetched: null,
  loading: false,
  error: null,
};

// Helper to fetch all rooms for a building with pagination
async function fetchAllRooms(buildingId: string): Promise<Room[]> {
  let allRooms: Room[] = [];
  let pageNum = 1;
  const pageSize = 100;
  let hasMore = true;
  while (hasMore) {
    const roomsRes = await roomServiceFind(buildingId, undefined, pageSize, pageNum);
    const rooms = roomsRes?.data?.rooms || [];
    allRooms = allRooms.concat(rooms);
    hasMore = rooms.length === pageSize;
    pageNum++;
  }
  return allRooms;
}

// Thunk to fetch all property data (apartments, buildings, rooms)
export const fetchAllPropertyData = createAsyncThunk(
  'propertyCache/fetchAllPropertyData',
  async ({orgId, userId}: {orgId: string; userId?: string}, {rejectWithValue}) => {
    try {
      // 1. Fetch all apartments with user permission filtering
      const apartmentsRes = await apartmentServiceFind(orgId, undefined, undefined, userId);
      if (!apartmentsRes?.data?.apartments) {
        return rejectWithValue('Failed to fetch apartments');
      }
      const apartments = apartmentsRes.data.apartments;

      // 2. Fetch buildings for all apartments
      const buildingsMap: {[apartmentId: string]: Building[]} = {};
      const buildingPromises = apartments.map(async apartment => {
        if (!apartment.id) return;

        const buildingsRes = await buildingServiceFind(undefined, undefined, [apartment.id]);
        if (buildingsRes?.data?.buildings) {
          buildingsMap[apartment.id] = buildingsRes.data.buildings;
        }
      });
      await Promise.all(buildingPromises);

      // 3. Fetch rooms for all buildings
      const roomsMap: {[buildingId: string]: Room[]} = {};
      const roomPromises: Promise<void>[] = [];
      // Collect all building IDs
      Object.values(buildingsMap).forEach(buildingList => {
        buildingList.forEach(building => {
          if (building.id) {
            const buildingId = building.id as string;
            roomPromises.push(
              fetchAllRooms(buildingId).then(allRooms => {
                roomsMap[buildingId] = allRooms;
              })
            );
          }
        });
      });
      await Promise.all(roomPromises);

      return {
        apartments,
        buildings: buildingsMap,
        rooms: roomsMap,
        timestamp: Date.now(),
      };
    } catch (error) {
      return rejectWithValue('Error fetching property data');
    }
  }
);

// Function to compute statistics from cached data
const computeStatistics = (state: PropertyCacheState) => {
  let totalRooms = 0;
  let occupiedRooms = 0;
  let vacantRooms = 0;

  // Calculate room statistics from cached data
  Object.values(state.rooms).forEach(roomList => {
    totalRooms += roomList.length;

    roomList.forEach(room => {
      if (room.status === RoomStatusEnum.Occupied) {
        occupiedRooms++;
      } else if (room.status === RoomStatusEnum.Vacant || room.status === RoomStatusEnum.Unknown) {
        vacantRooms++;
      }
    });
  });

  return {
    totalApartments: state.apartments.length,
    totalRooms,
    occupiedRooms,
    vacantRooms,
    totalTenants: 0, // We'll update this from the tenant slice
  };
};

export const propertyCacheSlice = createSlice({
  name: 'propertyCache',
  initialState,
  reducers: {
    clearCache: state => {
      state.apartments = [];
      state.buildings = {};
      state.rooms = {};
      state.lastFetched = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAllPropertyData.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllPropertyData.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.apartments = action.payload.apartments;
        state.buildings = action.payload.buildings;
        state.rooms = action.payload.rooms;
        state.lastFetched = action.payload.timestamp;

        // Compute statistics after data is loaded
        state.statistics = computeStatistics(state);
      })
      .addCase(fetchAllPropertyData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Selectors
export const selectPropertyStats = (state: RootState) => state.propertyCache.statistics;
export const selectPropertyLoading = (state: RootState) => state.propertyCache.loading;
export const selectLastFetched = (state: RootState) => state.propertyCache.lastFetched;
export const selectApartments = (state: RootState) => state.propertyCache.apartments;

export const {clearCache} = propertyCacheSlice.actions;
export default propertyCacheSlice.reducer;
